import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const PrivacyPage = () => {
  return (
    <div className="legal-page">
      <SEO
        title="Privacy Policy — No Logs, No KYC"
        description="PathProxy privacy policy. We never log browsing history, DNS queries, or content. No KYC required. Learn how we protect your data and privacy."
        path="/privacy"
      />
      <Navbar />

      <section className="legal-hero">
        <div className="container">
          <h1 className="legal-title">Privacy Policy</h1>
          <p className="legal-subtitle">Last updated: January 2026</p>
        </div>
      </section>

      <section className="legal-content">
        <div className="container">
          <div className="legal-text">
            <p className="legal-intro">
              At pathproxy, we take your privacy seriously. This Privacy Policy explains how we collect,
              use, and protect your personal information when you use our proxy services.
            </p>

            <h2>1. Information We Collect</h2>
            <p>We collect minimal information necessary to provide our services:</p>
            <ul>
              <li><strong>Account Information:</strong> Email address, username, and password (encrypted)</li>
              <li><strong>Usage Data:</strong> Bandwidth consumption for billing purposes</li>
              <li><strong>Performance Metrics:</strong> Connection speeds and uptime data (retained for maximum 6 hours, then automatically deleted)</li>
              <li><strong>Payment Information:</strong> Processed by third-party providers; we do not store card details</li>
            </ul>

            <h2>2. What We Do NOT Collect</h2>
            <p>To protect your privacy, we explicitly do NOT collect:</p>
            <ul>
              <li>Browsing history or websites visited</li>
              <li>Content of your internet traffic</li>
              <li>DNS queries or requests</li>
              <li>Original IP addresses after session ends</li>
              <li>Personal identification beyond account credentials</li>
            </ul>

            <h2>3. How We Use Your Information</h2>
            <p>The information we collect is used for:</p>
            <ul>
              <li>Providing and managing your proxy services</li>
              <li>Processing payments and maintaining billing records</li>
              <li>Improving service quality and performance</li>
              <li>Troubleshooting technical issues</li>
              <li>Sending important account notifications</li>
              <li>Responding to support requests</li>
            </ul>

            <h2>4. Payment Processing</h2>
            <p>
              We use secure third-party payment processors to handle transactions.
              These providers have their own privacy policies governing payment data:
            </p>
            <ul>
              <li><strong>Cryptocurrency:</strong> Bitcoin, Ethereum, and other crypto payments provide enhanced privacy</li>
              <li><strong>Card Payments:</strong> Processed through PCI-compliant payment gateways</li>
            </ul>
            <p>
              pathproxy does not have direct access to your full payment card information.
            </p>

            <h2>5. Data Retention</h2>
            <p>We retain your data according to the following schedule:</p>
            <ul>
              <li><strong>Account Credentials:</strong> Retained while your account is active</li>
              <li><strong>Usage Data:</strong> Retained for billing period plus 30 days</li>
              <li><strong>Performance Metrics:</strong> Maximum 6 hours, then automatically deleted</li>
              <li><strong>Support Tickets:</strong> Retained for 1 year after resolution</li>
            </ul>
            <p>
              Upon account deletion request, we will remove your personal data within 30 days,
              except where retention is required by law.
            </p>

            <h2>6. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your information:
            </p>
            <ul>
              <li>SSL/TLS encryption for all data transmission</li>
              <li>Encrypted password storage using secure hashing</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and authentication protocols</li>
            </ul>
            <p>
              While we strive to protect your data, no method of transmission over the internet
              or electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>

            <h2>7. Cookies and Tracking</h2>
            <p>
              Our website uses essential cookies for functionality:
            </p>
            <ul>
              <li><strong>Session Cookies:</strong> To keep you logged in</li>
              <li><strong>Preference Cookies:</strong> To remember your settings</li>
            </ul>
            <p>
              We do not use third-party tracking or advertising cookies.
            </p>

            <h2>8. Third-Party Services</h2>
            <p>
              We may use third-party services for specific functions. These services have their
              own privacy policies and we encourage you to review them. We do not sell or share
              your personal information with third parties for marketing purposes.
            </p>

            <h2>9. Law Enforcement Requests</h2>
            <p>
              We may disclose information when required by law or valid legal process.
              However, due to our proxy service architecture and minimal logging policy,
              we typically cannot identify specific users by IP address alone.
            </p>
            <p>
              We will notify you of any legal requests regarding your data unless prohibited by law.
            </p>

            <h2>10. Your Rights</h2>
            <p>You have the right to:</p>
            <ul>
              <li>Access your personal data we hold</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your account and data</li>
              <li>Export your data in a portable format</li>
              <li>Withdraw consent for data processing</li>
            </ul>
            <p>
              To exercise these rights, please contact our support team.
            </p>

            <h2>11. International Data Transfers</h2>
            <p>
              Your data may be processed in countries outside your residence.
              We ensure appropriate safeguards are in place to protect your information
              in accordance with this Privacy Policy.
            </p>

            <h2>12. Children's Privacy</h2>
            <p>
              Our services are not intended for individuals under 18 years of age.
              We do not knowingly collect personal information from children.
            </p>

            <h2>13. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Changes will be posted
              on this page with an updated revision date. Continued use of our services
              after changes constitutes acceptance of the updated policy.
            </p>

            <h2>14. Contact Us</h2>
            <p>
              For questions or concerns about this Privacy Policy or your data, contact us through:
            </p>
            <ul>
              <li>Our Discord server</li>
              <li>Telegram support</li>
              <li>Support ticket system in your dashboard</li>
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
